import { CheckmarkOutline, CloseOutline } from '@wfp/icons-react';
import { Button } from '@wfp/react';
import { DynamicIcon } from 'acr_ui/sources/components/DynamicIcon';
import sortBy from 'lodash/sortBy';
import React from 'react';

import CustomToggle from 'src/components/CustomToggle';
import { ASSET_STATUSES } from './constants';
import {
  IModalFooterProps,
  IReviewItemProps,
  IReviewNavProps,
  IReviewsListProps,
  IReviewStatusProps,
} from './types';
import { formatDate } from './utils';

export const ReviewNav: React.FC<IReviewNavProps> = ({
  assetsData,
  activeGroup,
  setActiveGroupHandler,
}) => {
  const groupKeys = assetsData.groups.map(group => group.name);

  return (
    <div className="button-switcher">
      {groupKeys.map(groupName => {
        return (
          <Button
            kind={activeGroup === groupName ? 'primary' : 'secondary'}
            key={groupName}
            onClick={() => setActiveGroupHandler(groupName)}
          >
            {groupName}
          </Button>
        );
      })}
    </div>
  );
};

export const ReviewStatusToggle: React.FC<IReviewStatusProps> = ({
  reviewStack,
  reviewStatus,
  showToggle,
  isFetching = false,
  handleReviewStatusToggle,
}) => {
  if (!showToggle) return null;

  const review = `${reviewStack}`;
  const isEnabled = !!reviewStatus; // reviewStatus exists when enabled, null when disabled

  const iconKwargs = {
      height: '1.2em',
    },
    buttonKwargs = {
      onClick: isFetching ? undefined : handleReviewStatusToggle,
      'data-review': review,
    };

  return (
    <footer className="review-status-change">
      <button
        {...buttonKwargs}
        data-action={0}
        className="enable-review"
        disabled={isEnabled || isFetching}
        style={{
          opacity: isEnabled ? 0.5 : 1,
          cursor: isEnabled || isFetching ? 'not-allowed' : 'pointer',
        }}
      >
        <CheckmarkOutline
          fill="#0a6eb4"
          description=""
          {...iconKwargs}
          height="20"
          width="20"
        />
      </button>
      <button
        {...buttonKwargs}
        data-action={1}
        className="disable-review"
        disabled={!isEnabled || isFetching}
        style={{
          opacity: !isEnabled ? 0.5 : 1,
          cursor: !isEnabled || isFetching ? 'not-allowed' : 'pointer',
        }}
      >
        <CloseOutline
          fill="#c5192d"
          description=""
          {...iconKwargs}
          height="20"
          width="20"
        />
      </button>
    </footer>
  );
};

export const ReviewStatus: React.FC<IReviewStatusProps> = ({
  reviewStack,
  reviewStatus,
  showToggle,
  isFetching = false,
  handleReviewStatusToggle,
}) => {
  const reviewStatusToggle = showToggle ? (
    <ReviewStatusToggle
      reviewStack={reviewStack}
      reviewStatus={reviewStatus}
      showToggle={showToggle}
      isFetching={isFetching}
      handleReviewStatusToggle={handleReviewStatusToggle}
    />
  ) : null;

  return (
    (!!reviewStatus || reviewStatusToggle) && (
      <div className="review-status">
        {reviewStatus && (
          <>
            <span className="user">{reviewStatus.user}</span>
            <span className="date">{formatDate(reviewStatus.date)}</span>
          </>
        )}
        {reviewStatusToggle}
      </div>
    )
  );
};

export const ReviewItem: React.FC<IReviewItemProps> = ({
  review,
  reviewId,
  reviewStack,
  reviewStatus,
  showToggle,
  isFetching = false,
  handleReviewStatusToggle,
  isEditMode = false,
  toggled,
  handleIconVisibilityToggle,
}) => {
  if (isEditMode) {
    return (
      <div className="review-item" key={reviewId}>
        <figure>
          <DynamicIcon
            url={review.icon_url}
            width={32}
            height={32}
            customClass={`review-icon-aws ${
              review.icon === 'icon-sdg' ? 'icon-sdg ' : ''
            }`}
            disableAutoFillPaths={['icon-sdg', 'icon-environment'].includes(
              review.icon,
            )}
          />
          <figcaption>{review.name}</figcaption>
        </figure>
        <div className="icon-visibility-control">
          <CustomToggle
            id={reviewId}
            labelB=""
            labelA=""
            toggled={toggled}
            onChange={evt =>
              handleIconVisibilityToggle(evt.target.checked, reviewId)
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`review-item${reviewStatus ? ' checked' : ''}`}
      key={reviewId}
    >
      <figure>
        <DynamicIcon
          url={review.icon_url}
          width={32}
          height={32}
          disableAutoFillPaths={['icon-sdg', 'icon-environment'].includes(
            review.icon,
          )}
          customClass={`review-icon-aws ${
            review.icon === 'icon-sdg' ? 'icon-sdg ' : ''
          } ${reviewStatus ? 'review-status' : ''}`}
        />
        <figcaption>{review.name}</figcaption>
      </figure>
      <ReviewStatus
        reviewStack={reviewStack}
        reviewStatus={reviewStatus}
        showToggle={showToggle}
        isFetching={isFetching}
        handleReviewStatusToggle={handleReviewStatusToggle}
      />
    </div>
  );
};

export const ReviewsList: React.FC<IReviewsListProps> = ({
  assetsData,
  activeGroup,
  editReviewsMode,
  toggleIconVisibility,
  handleIconVisibilityToggle,
  props,
  isFetching = false,
  handleReviewStatusToggle,
}) => {
  const sortedReviews = assetsData?.reviews
    ? sortBy(assetsData.reviews, 'name')
    : [];

  const groupKeys = assetsData.groups.map(group => group.name),
    activeGroupIndex = groupKeys.indexOf(activeGroup),
    activeGroupName =
      activeGroupIndex !== -1
        ? assetsData.groups[activeGroupIndex].name
        : groupKeys.length > 0
        ? assetsData.groups[0].name
        : null;

  const listClasses = ['reviews-list'];

  if (!activeGroupName) return null;

  if (editReviewsMode) {
    listClasses.push('being-edited');
    const selectedGroup = [],
      deselectedGroup = [];
    //sort assets by alphabetical order
    const assetsDataArray = sortBy(Object.entries(assetsData.reviews), [
      function (o) {
        return o[1].name;
      },
    ]);
    const idsInOrder = assetsDataArray.map(item => {
      return item[0];
    });

    for (const reviewId in idsInOrder) {
      const review = assetsData.reviews[idsInOrder[reviewId]],
        initiallySelected =
          typeof review.groups[activeGroupName] !== 'undefined',
        currentContainer = initiallySelected ? selectedGroup : deselectedGroup;

      const toggled = (toggleIconVisibility[activeGroup] || {}).hasOwnProperty(
        idsInOrder[reviewId],
      )
        ? toggleIconVisibility[activeGroup][idsInOrder[reviewId]]
        : typeof review.groups[activeGroupName] !== 'undefined';

      currentContainer.push(
        <ReviewItem
          key={idsInOrder[reviewId]}
          review={review}
          reviewId={idsInOrder[reviewId]}
          isEditMode={true}
          toggled={toggled}
          handleIconVisibilityToggle={handleIconVisibilityToggle}
        />,
      );
    }

    return (
      <>
        {selectedGroup.length > 0 && (
          <>
            <h5 className="group-title">Selected functions</h5>
            <div className={['selected', ...listClasses].join(' ')}>
              {selectedGroup}
            </div>
          </>
        )}
        {deselectedGroup.length > 0 && (
          <>
            <h5 className="group-title">Available functions</h5>
            <div className={['deselected', ...listClasses].join(' ')}>
              {deselectedGroup}
            </div>
          </>
        )}
      </>
    );
  } else {
    const activeGroupComponents = [];
    const reviewStatusesData = props[ASSET_STATUSES].data
        ? props[ASSET_STATUSES].data.actions
        : null,
      showToggle =
        !!reviewStatusesData &&
        !!props.hasPermissionToMark &&
        props.sectionData?.data?.permissions?.can_change_reviewaction;

    for (const reviewId in sortedReviews) {
      const review = sortedReviews[reviewId],
        reviewStack = review.groups[activeGroupName];

      if (typeof reviewStack === 'undefined') continue;

      const reviewStatus = reviewStatusesData
        ? reviewStatusesData[reviewStack]
        : null;

      activeGroupComponents.push(
        <ReviewItem
          key={reviewId}
          review={review}
          reviewId={reviewId}
          reviewStack={reviewStack}
          reviewStatus={reviewStatus}
          showToggle={showToggle}
          isFetching={isFetching}
          handleReviewStatusToggle={handleReviewStatusToggle}
          isEditMode={false}
        />,
      );
    }
    return <div className={listClasses.join(' ')}>{activeGroupComponents}</div>;
  }
};

export const ModalFooter: React.FC<IModalFooterProps> = ({
  editReviewsMode,
  handleSubmitEditedIcons,
  handleCancelEditIcons,
  handleIconVisibilityToggleAll,
  handleStartEditIcons,
}) => {
  return editReviewsMode ? (
    <>
      <Button
        kind="secondary"
        onClick={() => handleIconVisibilityToggleAll(false)}
        data-test-id="deselect-all-button"
        className="deselect-btn"
      >
        Deselect all
      </Button>
      <Button
        kind="secondary"
        onClick={() => handleIconVisibilityToggleAll(true)}
        data-test-id="select-all-button"
      >
        Select all
      </Button>
      <Button
        className="float-right"
        onClick={handleSubmitEditedIcons}
        data-test-id="save-button"
      >
        Save
      </Button>
      <Button
        className="float-right"
        onClick={handleCancelEditIcons}
        data-test-id="cancel-button"
      >
        Cancel
      </Button>
    </>
  ) : (
    <Button
      className="float-right"
      onClick={handleStartEditIcons}
      data-test-id="add-remove-icons-button"
    >
      Add/Remove functions
    </Button>
  );
};
